import React, { useState } from "react";
import { DoorOpen, Plus, Pencil, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccCard, ccBtnPink, EmptyState } from "../care/ui";

// Rooms: owners/staff create rooms and assign children; parents see read-only room info via scoping
export default function Rooms({ ctx }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ name: "", capacity: "", ageGroup: "" });
  const rooms = ctx.rooms || [];
  const children = ctx.allChildren || [];

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", capacity: "", ageGroup: "" });
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, capacity: String(r.capacity || ""), ageGroup: r.ageGroup || "" });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { name: form.name.trim(), capacity: parseInt(form.capacity, 10) || 0, ageGroup: form.ageGroup.trim() };
    try {
      if (editing) {
        await ctx.ws.updateRoom(editing.id, payload);
        toast.success("Room updated.");
      } else {
        await ctx.ws.addRoom(payload);
        toast.success("Room created.");
      }
      setShowForm(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (rid) => {
    try {
      await ctx.ws.deleteRoom(rid);
      toast.success("Room deleted — children unassigned.");
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const assign = async (rid, childId) => {
    if (!childId) return;
    try {
      await ctx.ws.assignChildToRoom(rid, childId);
      toast.success("Child assigned to room.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const unassigned = children.filter((c) => !c.roomId);

  return (
    <div data-testid="rooms-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[11px] text-[#9CA3AF] font-medium">Create rooms and assign children. Capacity is enforced automatically.</p>
        <button onClick={openNew} data-testid="room-add-btn" className={ccBtnPink}>
          <Plus className="w-4 h-4 inline mr-1 -mt-0.5" /> New Room
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="mb-5 p-5 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="room-form">
          <div className="sm:col-span-2">
            <label className={ccLabel}>Room Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ccInput} placeholder="e.g. Rainbow Room" data-testid="room-name-input" />
          </div>
          <div>
            <label className={ccLabel}>Capacity</label>
            <input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className={ccInput} data-testid="room-capacity-input" />
          </div>
          <div>
            <label className={ccLabel}>Age Group</label>
            <input value={form.ageGroup} onChange={(e) => setForm({ ...form, ageGroup: e.target.value })} className={ccInput} placeholder="e.g. Toddlers" data-testid="room-agegroup-input" />
          </div>
          <div className="sm:col-span-4 flex gap-2">
            <button type="submit" className={ccBtnPink} data-testid="room-save-btn">{editing ? "Save Room" : "Create Room"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 bg-white border border-[#FCE6E9] text-[#6B7280] rounded-full text-xs font-bold" data-testid="room-cancel-btn">Cancel</button>
          </div>
        </form>
      )}

      {rooms.length === 0 ? (
        <EmptyState text="No rooms yet — create your first room to start assigning children." testId="rooms-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rooms.map((r) => {
            const occupants = children.filter((c) => c.roomId === r.id);
            const full = r.capacity > 0 && occupants.length >= r.capacity;
            return (
              <div key={r.id} className={`${ccCard} p-5`} data-testid={`room-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-[#E6F2FA] flex items-center justify-center">
                      <DoorOpen className="w-4 h-4 text-[#5CB3DC]" />
                    </span>
                    <div>
                      <h4 className="text-sm font-extrabold font-heading text-[#374151]" data-testid={`room-name-${r.id}`}>{r.name}</h4>
                      <p className="text-[11px] text-[#9CA3AF] font-medium">{r.ageGroup || "All ages"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(r)} data-testid={`room-edit-${r.id}`} className="w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-[#5CB3DC] hover:bg-[#E6F2FA] transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {confirmDelete === r.id ? (
                      <button onClick={() => remove(r.id)} data-testid={`room-delete-confirm-${r.id}`} className="px-2.5 py-1 bg-rose-500 text-white text-[10px] font-bold rounded-full">Confirm?</button>
                    ) : (
                      <button onClick={() => setConfirmDelete(r.id)} data-testid={`room-delete-${r.id}`} className="w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-rose-400 hover:bg-rose-50 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <p className={`text-[11px] font-extrabold mb-3 flex items-center gap-1.5 ${full ? "text-rose-500" : "text-[#2F6031]"}`} data-testid={`room-occupancy-${r.id}`}>
                  <Users className="w-3.5 h-3.5" /> {occupants.length}{r.capacity ? `/${r.capacity}` : ""} children {full && "· FULL"}
                </p>
                <div className="flex gap-1.5 flex-wrap mb-3">
                  {occupants.map((c) => (
                    <span key={c.id} className="px-2 py-0.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-[10px] font-bold rounded-full flex items-center gap-1" data-testid={`room-child-${r.id}-${c.id}`}>
                      {c.firstName} {c.lastName}
                      <button onClick={() => ctx.ws.unassignChild(c.id).catch((e) => toast.error(e.message))} className="hover:text-rose-500" data-testid={`room-unassign-${r.id}-${c.id}`}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                  {occupants.length === 0 && <span className="text-[11px] text-[#9CA3AF] font-medium">No children assigned</span>}
                </div>
                {!full && unassigned.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => assign(r.id, e.target.value)}
                    className={ccInput}
                    data-testid={`room-assign-select-${r.id}`}
                  >
                    <option value="">Assign a child…</option>
                    {unassigned.map((c) => (
                      <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
